const s = require("./borderDataOverview.module.scss");

import { default as React, useEffect, useContext, useState } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import List from "./list";
import httpService from "core/infrastructure/http";
import { GetAtcResponse, GetScheduledExchangesResponse, GetPriceSpreadResponse, GetCongestionIncomeResponse, GetLtnResponse, GetShadowAuctionAtcResponse, GetIntradayAtcResponse, AtcDto, ScheduledExchangesDto, PriceSpreadDto, CongestionIncomeDto, LtnDto, IntradayAtcDto, ShadowAuctionAtcDto } from "core/backend/data";
import SheetDataPart from "components/sheetDataPart";
import SheetHeader from "components/sheetHeader";
import BorderName from "components/borderName";
import { ApplicationState } from "core/app";
import classNames from "classnames";
import { keys } from "ts-transformer-keys";
import { MultiSelectItem } from "components/multiselect";
import { PageList } from "core/pages/pageList";

interface OwnState {
    applicationState: ApplicationState;
}
export const senderId = "borderDataOverview";
export const page_config: PageList =
{
    apiUrl: "",
    webUrl: "borderDataOverview",
    menu: "menu:menuBorderDataOverview",
    title: "menu:titleBorderDataOverview",
    apiType: "data",
    response: "",
    dto: "",
    parameters: "",
    dateSteps: "D",
    getDefaultToDateTime: (applicationState: ApplicationState) => applicationState.getUntilFor1Day()
};

type PageProps = OwnState & WithTranslation;

const Page = ({ t, applicationState }: PageProps) => {

    const keysInDto: string[] =
        keys<ScheduledExchangesDto>().filter(k => k.startsWith("border_")).map(k => k.toString()) //specify type
            .concat(keys<AtcDto>().filter(k => k.startsWith("border_")))
            .concat(keys<PriceSpreadDto>().filter(k => k.startsWith("border_")))
            .concat(keys<CongestionIncomeDto>().filter(k => k.startsWith("border_")))
            .concat(keys<LtnDto>().filter(k => k.startsWith("border_")))
            .concat(keys<ShadowAuctionAtcDto>().filter(k => k.startsWith("border_")))
            .concat(keys<IntradayAtcDto>().filter(k => k.startsWith("initial_")).map(k => k.replace("initial_", "border_")))
            .filter((value, index, self) => self.indexOf(value) === index) // =distinct
            .sort();
    const dtoBorders = applicationState.selectedBorders.filter(f => f.selected && f.isDisplayed);
    const [selectedBorder, setSelectedBorder] = useState(dtoBorders.length == 0 ? "" : dtoBorders[0].id);

    const initialAtcData: AtcDto[] = [];
    const initialQTYData: ScheduledExchangesDto[] = [];
    const initialPsData: PriceSpreadDto[] = [];
    const initialCiData: CongestionIncomeDto[] = [];
    const initialLtnData: LtnDto[] = [];
    const initialSAtcData: ShadowAuctionAtcDto[] = [];
    const initialIAtcData: IntradayAtcDto[] = [];
    const initialDates: Date[] = [];

    const [atcData, setAtcData] = useState(initialAtcData);
    const [qtyData, setQtyData] = useState(initialQTYData);
    const [psData, setPsData] = useState(initialPsData);
    const [ciData, setCiData] = useState(initialCiData);
    const [ltnData, setLtnData] = useState(initialLtnData);
    const [sAtcData, setSAtcData] = useState(initialSAtcData);
    const [iAtcData, setIAtcData] = useState(initialIAtcData);
    const [isLoading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [dates, setDates] = useState(initialDates);

    const loadAtc = async () => {
        const response = await httpService.apiPost<GetAtcResponse>({
            applicationState: applicationState,
            route: applicationState.apiService + "/" + page_config.apiType + "/atc",
            query: {
                FromUtc: applicationState.getRootUtcTime().toISOString(),
                ToUtc: applicationState.getUntilFor1Day().toISOString()
            },
            senderId: senderId
        })();

        if (response.ok) {
            setAtcData(response.data.data);
            setError("");
        } else {
            setError(response.error || t("common:UnknownError"));
        }
    };
    const loadQty = async () => {
        const response = await httpService.apiPost<GetScheduledExchangesResponse>({
            applicationState: applicationState,
            route: applicationState.apiService + "/" + page_config.apiType + "/scheduledExchanges",
            query: {
                FromUtc: applicationState.getRootUtcTime().toISOString(),
                ToUtc: applicationState.getUntilFor1Day().toISOString()
            },
            senderId: senderId
        })();

        if (response.ok) {
            setQtyData(response.data.data);
            setError("");
        } else {
            setError(response.error || t("common:UnknownError"));
        }
    };
    const loadPriceSpread = async () => {
        const response = await httpService.apiPost<GetPriceSpreadResponse>({
            applicationState: applicationState,
            route: applicationState.apiService + "/" + page_config.apiType + "/priceSpread",
            query: {
                FromUtc: applicationState.getRootUtcTime().toISOString(),
                ToUtc: applicationState.getUntilFor1Day().toISOString()
            },
            senderId: senderId
        })();

        if (response.ok) {
            setPsData(response.data.data);
            setError("");
        } else {
            setError(response.error || t("common:UnknownError"));
        }
    };
    const loadCongestionIncome = async () => {
        const response = await httpService.apiPost<GetCongestionIncomeResponse>({
            applicationState: applicationState,
            route: applicationState.apiService + "/" + page_config.apiType + "/congestionIncome",
            query: {
                FromUtc: applicationState.getRootUtcTime().toISOString(),
                ToUtc: applicationState.getUntilFor1Day().toISOString()
            },
            senderId: senderId
        })();

        if (response.ok) {
            setCiData(response.data.data);
            setError("");
        } else {
            setError(response.error || t("common:UnknownError"));
        }
    };
    const loadLtn = async () => {
        const response = await httpService.apiPost<GetLtnResponse>({
            applicationState: applicationState,
            route: applicationState.apiService + "/" + page_config.apiType + "/ltn",
            query: {
                FromUtc: applicationState.getRootUtcTime().toISOString(),
                ToUtc: applicationState.getUntilFor1Day().toISOString()
            },
            senderId: senderId
        })();

        if (response.ok) {
            setLtnData(response.data.data);
            setError("");
        } else {
            setError(response.error || t("common:UnknownError"));
        }
    };
    const loadSAtc = async () => {
        const response = await httpService.apiPost<GetShadowAuctionAtcResponse>({
            applicationState: applicationState,
            route: applicationState.apiService + "/" + page_config.apiType + "/shadowAuctionAtc",
            query: {
                FromUtc: applicationState.getRootUtcTime().toISOString(),
                ToUtc: applicationState.getUntilFor1Day().toISOString()
            },
            senderId: senderId
        })();

        if (response.ok) {
            setSAtcData(response.data.data);
            setError("");
        } else {
            setError(response.error || t("common:UnknownError"));
        }
    };
    const loadIAtc = async () => {
        const response = await httpService.apiPost<GetIntradayAtcResponse>({
            applicationState: applicationState,
            route: applicationState.apiService + "/" + page_config.apiType + "/intradayAtc",
            query: {
                FromUtc: applicationState.getRootUtcTime().toISOString(),
                ToUtc: applicationState.getUntilFor1Day().toISOString()
            },
            senderId: senderId
        })();

        if (response.ok) {
            setIAtcData(response.data.data);
            setError("");
        } else {
            setError(response.error || t("common:UnknownError"));
        }
    };

    useEffect(() => {
        (async function runAsync() {
            applicationState.handleInputAvailablilityChange(page_config.dateSteps, true, false, keysInDto);
            setLoading(true);
            setAtcData([]);
            setQtyData([]);
            setPsData([]);
            setCiData([]);
            setLtnData([]);
            setSAtcData([]);
            setIAtcData([]);

            const rootDate = applicationState.getRootUtcTime();
            let date = rootDate;
            let xxx: Date[] = [];
            while (rootDate.getDate() === date.getDate()) {
                xxx = [...xxx, new Date(date)];
                date = new Date(date.setHours(date.getHours() + 1));
            }
            setDates(xxx);

            //we cannot use Promis.All to request both data at the same time because of IP LIMIT
            //await Promise.all([
            await loadAtc();
            await loadQty();
            await loadPriceSpread();
            await loadCongestionIncome();
            await loadLtn();
            await loadSAtc();
            await loadIAtc();
            //]);
            setLoading(false);
        })();
    }, [applicationState.selectedDate]);

    return (
        <>
            <SheetHeader showDownload={false} applicationState={applicationState} title={t(page_config.title)} pageConfig={page_config} />
            <div className="subHeader">
                {dtoBorders.map(b => <div className={classNames(s.selectableBorder, b.id === selectedBorder ? s.selected : "")} key={b.id} onClick={() => setSelectedBorder(b.id)}><BorderName text={b.text} /></div>)}
            </div>
            <SheetDataPart isLoading={isLoading} dataRows={atcData.length + qtyData.length + psData.length + ciData.length + ltnData.length + sAtcData.length + iAtcData.length} error={error}>
                <List border={selectedBorder} dates={dates} atcData={atcData} qtyData={qtyData} psData={psData} ciData={ciData} ltnData={ltnData} sAtcData={sAtcData} iAtcData={iAtcData} />
            </SheetDataPart>
        </>
    );
};

export default withTranslation("page_borderDataOverview")(Page);
